import React, { useState } from 'react';

// Components
import { ConfigContext, PillTabsHeader, TournamentEvents } from '@velinsi/smp-widgets-ui';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';

// Hooks
import { useEvents } from '../../../../../packages/hooks/multi-sport/useEvents';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';

// Utils
import dayjs from 'dayjs';
import { defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils';

// Constants
import { ALL, POPULAR, statusFilters } from '../../../../../packages/constants/statusFilters';
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';
import { TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';

// Types
import { BaseWidgetProps } from '../../../../../packages/types';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';
import { IMultiSportLivescoreConfig } from '../../../../../packages/types/multisport';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';

export type LivescoreWidgetsProps = BaseWidgetProps<IMultiSportLivescoreConfig>;

export const LivescoreWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: LivescoreWidgetsProps) => {
  const showDateDropdown = config?.dataHeaderDisplay === null || undefined || config?.dataHeaderDisplay === true;
  const date = dayjs(config?.dataDate?.date, config?.dataDate?.dateFormat).toDate();

  const [selectedFilter, setSelectedFilter] = useState<string>(config?.dataHeaderDefaultOption ? config?.dataHeaderDefaultOption : ALL);
  const [selectedDate, setSelectedDate] = useState<Date>(date);
  const { ref } = useRootNodeRef();

  const {
    handleClick,
    widgetData: { livescore, isLoading },
  } = useEvents({
    subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsMarket: config?.dataOddsMarket,
    statusFilter: selectedFilter,
    date: selectedDate,
    utcOffset: dayjs.tz(selectedDate, sdkOptions.dataConfigTimezone).format('Z'),
    dataWidgetCuid: cuid,
    sdkOptions,
    dataTeamNameType: config?.dataTeamNameType,
    dataLabels: config?.dataLabels,
    dataEntityLinks: config?.dataEntityLinks,
    dataPopularList: config?.dataPopularList,
  });

  const pillTabOptions = statusFilters
    .map((option) => ({
      label: config?.dataLabels?.[option.toLowerCase()] || option,
      value: option,
    }))
    .filter(({ value }) => config?.dataPopularList || value !== POPULAR);

  const handlePillTabChange = (value: string) => {
    setSelectedFilter(value);
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={isLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-primaryBackgroundColor)'
      >
        <PillTabsHeader
          selectedDate={selectedDate}
          dataDate={config?.dataDate}
          selectedPillTab={selectedFilter}
          pillTabOptions={pillTabOptions}
          displayPillTabs={config?.dataOptionsDisplay}
          displayDateDropdown={showDateDropdown}
          handleClick={handleClick}
          handlePillTabChange={handlePillTabChange}
          handleDateChange={setSelectedDate}
        />
        <TournamentEvents data={livescore} handleClick={handleClick} selectedFilter={selectedFilter} />
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
